import type { ColumnFilter } from "@tanstack/react-table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { BookingsTableInstanceProvider } from "@/components/shared/bookings-table/bookings-table-instance-provider";
import { useTenant } from "@/providers/session";
import { useGetFastlaneReview } from "@/queries/fastlane/get-review";
import { useUpdateLineItemReviewed } from "@/queries/line-items/update";

import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { useUpdateInvoice } from "@/queries/invoices/update";
import { useQueryState } from "nuqs";
import { columns as _columns } from "./columns";
import { DataTable } from "./data-table";

type Props = {
  tableName: string;
  initialColumnFilters?: ColumnFilter[];
  invoiceType?: InvoiceTypeString;
};

export const Fastlane = ({
  tableName,
  initialColumnFilters,
  invoiceType,
}: Props) => {
  const { t } = useTranslation();
  const { hasCostCenters } = useTenant();
  const [supplierId, setSupplierId] = useQueryState("supplier_id", {
    defaultValue: undefined,
    parse: (value) => value || "",
    serialize: (value) => value || "",
    history: "replace",
  });
  const initializedRef = useRef(false);

  const [doneSupplierIds, setDoneSupplierIds] = useState<Set<string>>(
    new Set(),
  );

  const {
    invoices,
    suppliers: _suppliers,
    error,
    loading,
  } = useGetFastlaneReview({
    origin: tableName,
    supplierId: supplierId ?? undefined,
    invoiceType,
  });

  const tableData = useMemo(() => {
    return invoices;
  }, [invoices]);

  const [updateInvoice] = useUpdateInvoice();

  const updateLineItem = useUpdateLineItemReviewed();

  const handleSetSupplierDone = useCallback(
    (supplierId: string) => {
      if (doneSupplierIds.has(supplierId)) return;

      setDoneSupplierIds((prev) => {
        prev.add(supplierId);
        return prev;
      });
    },
    [doneSupplierIds],
  );

  const handleSetSupplierNotDone = useCallback(
    (supplierId: string) => {
      setDoneSupplierIds((prev) => {
        prev.delete(supplierId);
        return prev;
      });
    },
    [doneSupplierIds],
  );

  const handleSupplierDone = useCallback(
    async (ids: string[], supplierId: string, onUndo?: () => void) => {
      const handleUndo = () => {
        handleSetSupplierNotDone(supplierId);
        updateLineItem(
          ids,
          {
            accountReviewed: false,
          },
          {},
        );
        onUndo?.();
      };

      handleSetSupplierDone(supplierId);
      await updateLineItem(
        ids,
        {
          accountReviewed: true,
        },
        {
          onUndo: handleUndo,
        },
      );
    },
    [updateLineItem, supplierId],
  );

  const handleNotABooking = async (
    invoiceId: string,
    lineItemIds: string[],
  ) => {
    await updateLineItem(
      lineItemIds,
      {
        accountReviewed: true,
      },
      {},
    );

    await updateInvoice({ invoiceId, isBooking: false });
  };

  const initialColumnVisibility = useMemo(
    () => ({
      supplierId: false,
      isDuplicate: false,
      isBooking: false,
      accountReviewed: false,
      invoiceNo: false,
      invoiceStatus: false,
    }),
    [],
  );

  const initialSorting = useMemo(
    () => [
      {
        id: "invoiceNo",
        desc: false,
      },
    ],
    [],
  );

  const columns = useMemo(() => {
    return _columns(hasCostCenters ?? false);
  }, [hasCostCenters]);

  const handleSupplierChange = useCallback(
    (supplierId: string | null, origin: string) => {
      setSupplierId(supplierId);
    },
    [setSupplierId],
  );

  const suppliers = useMemo(() => {
    return _suppliers.filter(
      (supplier) => !doneSupplierIds.has(supplier.supplierId ?? ""),
    );
  }, [_suppliers, doneSupplierIds]);

  useEffect(() => {
    if (initializedRef.current === false && suppliers.length > 0) {
      if (supplierId) {
        const supplier = suppliers.find(
          (supplier) => supplier.supplierId === supplierId,
        );

        if (supplier) {
          handleSupplierChange(supplierId, "init");
        } else {
          handleSupplierChange(suppliers[0].supplierId! ?? "unknown", "init");
        }
      } else {
        handleSupplierChange(suppliers[0].supplierId! ?? "unknown", "init");
      }
      initializedRef.current = true;
    }
  }, [supplierId, suppliers, initializedRef.current]);

  return (
    <BookingsTableInstanceProvider
      tableName={tableName}
      columns={columns}
      tableData={tableData}
      initialColumnFilters={initialColumnFilters}
      initialColumnVisibility={initialColumnVisibility}
      initialSorting={initialSorting}
      clickableColumns={[]}
      keepExpandedRowsOnDepthLevel={[0]}
    >
      <DataTable
        loading={loading}
        suppliers={suppliers}
        supplierId={supplierId ?? ""}
        onSupplierDone={handleSupplierDone}
        onNotABooking={handleNotABooking}
        onSupplierChange={handleSupplierChange}
        error={error ? t("invoices-network-error") : undefined}
        invoiceType={invoiceType}
      />
    </BookingsTableInstanceProvider>
  );
};
